import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Music, Play, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Avatar } from "@/components/PostCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { motion, AnimatePresence } from "framer-motion";

export default function SearchBar() {
  const navigate = useNavigate();
  const { playTrack } = usePlayer();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);        // desktop dropdown
  const [mobileOpen, setMobileOpen] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q.trim()) { setRes(null); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); setOpen(true); } catch {}
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const reset = () => { setQ(""); setRes(null); setOpen(false); setMobileOpen(false); };
  const goArtist = (a) => { reset(); navigate(`/artist/${a.id}`); };
  const play = (t) => { reset(); playTrack(t, [t]); };
  const empty = res && res.artists.length === 0 && res.tracks.length === 0;

  const Results = ({ testid }) => (
    <div data-testid={testid} className="overflow-y-auto max-h-[70vh] sm:max-h-96 hide-scrollbar">
      {empty && <p className="p-4 text-sm text-zinc-500">No results for "{q}"</p>}
      {res && res.artists.length > 0 && (
        <div className="p-2">
          <p className="px-2 py-1 text-[10px] font-mono-nu uppercase tracking-widest text-zinc-500">Artists</p>
          {res.artists.map((a) => (
            <button key={a.id} onClick={() => goArtist(a)} data-testid="search-artist-result"
              className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 text-left">
              <Avatar artist={a} size={34} />
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate flex items-center gap-1">{a.artist_name}<VerifiedBadge show={a.is_subscribed} size={13} /></p>
                <p className="text-xs text-zinc-500 truncate">{a.genre || `@${a.handle}`}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {res && res.tracks.length > 0 && (
        <div className="p-2 border-t border-white/10">
          <p className="px-2 py-1 text-[10px] font-mono-nu uppercase tracking-widest text-zinc-500">Tracks</p>
          {res.tracks.map((t) => (
            <button key={t.id} onClick={() => play(t)} data-testid="search-track-result"
              className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 text-left">
              {t.cover_url ? <img src={mediaUrl(t.cover_url)} alt="" className="w-9 h-9 rounded object-cover" />
                : <span className="w-9 h-9 rounded bg-emerald-500/15 flex items-center justify-center"><Music className="w-4 h-4 text-emerald-400" /></span>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{t.title}</p>
                <p className="text-xs text-zinc-500 truncate">{t.artist?.artist_name}</p>
              </div>
              <Play className="w-4 h-4 text-emerald-400 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop inline search */}
      <div ref={boxRef} className="relative w-full max-w-xs hidden sm:block" data-testid="search-bar">
        <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-full px-3 py-2 focus-within:border-emerald-500/50 transition-colors">
          <Search className="w-4 h-4 text-zinc-500 shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
            placeholder="Search artists, tracks, genres…" data-testid="search-input"
            className="bg-transparent outline-none text-sm w-full placeholder:text-zinc-600" />
        </div>
        {open && res && (
          <div className="absolute top-12 left-0 right-0 bg-[#181824] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50">
            <Results testid="search-results" />
          </div>
        )}
      </div>

      {/* Mobile search toggle */}
      <button onClick={() => setMobileOpen(true)} data-testid="mobile-search-btn"
        className="sm:hidden w-9 h-9 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
        <Search className="w-5 h-5" />
      </button>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="sm:hidden fixed inset-0 z-[70] bg-[#0A0A0E]/98 backdrop-blur-lg" data-testid="mobile-search-overlay">
            <div className="p-4 flex items-center gap-2 border-b border-white/10">
              <div className="flex-1 flex items-center gap-2 bg-black/40 border border-white/10 rounded-full px-3 py-2.5 focus-within:border-emerald-500/50">
                <Search className="w-4 h-4 text-zinc-500 shrink-0" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search artists, tracks, genres…" data-testid="search-input-mobile"
                  className="bg-transparent outline-none text-sm w-full placeholder:text-zinc-600" />
              </div>
              <button onClick={reset} data-testid="mobile-search-close" className="w-9 h-9 rounded-full flex items-center justify-center text-zinc-400"><X className="w-5 h-5" /></button>
            </div>
            <Results testid="search-results-mobile" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
